export interface CandidateSchool {
  id: string;
  name: string;
}

export interface CandidateProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string | null;
  vehicleType: string;
  status: string;
  hasPassword?: boolean;
  school: CandidateSchool | null;
  /** Réglages d'accès de l'auto-école appliqués à l'espace élève. */
  access?: {
    canViewPayments: boolean;
    canCancelLessons: boolean;
    canBookHolidays: boolean;
    /** Réservation limitée au moniteur référent : son nom tel que l'élève le voit, sinon `null`. */
    bookingReferent?: string | null;
  };
}

export interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  candidate: CandidateProfile;
}

export type VerifyResponse = SessionResponse;

export interface CandidateSlot {
  id: string;
  startsAt: string;
  endsAt: string;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
}

export interface CandidateLesson {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  lessonType: string | null;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
  bookedOnline: boolean;
  /** Émargement : présence signée (leçon honorée). */
  honored: boolean;
  attendanceSignedAt: string | null;
}

export interface CandidatePayment {
  id: string;
  date: string;
  label: string;
  type: string;
  amount: number;
  /** Facture réglée par ce paiement (si le libellé y renvoie), pour le PDF. */
  invoiceId?: string | null;
}

/** État de règlement calculé d'une facture émise (F39) : le statut enregistré ne change pas. */
export interface InvoiceSettlement {
  state: 'paid' | 'partial' | 'due' | 'credited';
  outstandingCents: number;
  dueOn: string;
  overdue: boolean;
}

export interface CandidateInvoice {
  id: string;
  number: string;
  title: string;
  issueDate: string;
  status: string;
  totalTtc: number;
  /** Absent si l'auto-école ne montre pas les paiements. */
  settlement?: InvoiceSettlement | null;
}

/** Facture à régler : `amountDue` est le reste dû (encaissements et avoirs déduits). */
export interface PayableInvoice extends CandidateInvoice {
  amountDue: number;
}

export interface PayableInvoicesResponse {
  paymentEnabled: boolean;
  invoices: PayableInvoice[];
}

export interface CandidateContract {
  id: string;
  number: string;
  status: string;
  signedAt: string | null;
}

export interface CandidateDocuments {
  invoices: CandidateInvoice[];
  contracts: CandidateContract[];
}

/** Devis ou commande envoyé en signature électronique par l'auto-école. */
export interface CandidateSignatureDocument {
  requestId: string;
  kind: 'quote' | 'order';
  documentName: string;
  expiresAt: string;
  /** L'élève a déjà signé ; une autre signature est attendue. */
  signed: boolean;
}

/** Défi à vérifier sur la page de signature après envoi du code par e-mail. */
export interface SignatureChallenge {
  challengeId: string;
  email: string;
  documentKind: string;
}

export type DocumentRequestStatus =
  | 'requested'
  | 'provided'
  | 'validated'
  | 'rejected'
  | 'cancelled';

export interface CandidateDocumentRequest {
  id: string;
  docType: string;
  label: string;
  status: DocumentRequestStatus;
  note: string | null;
  requestedAt: string;
  providedAt: string | null;
  hasFile: boolean;
  fileName: string | null;
  /** Motif d'une pièce refusée par l'auto-école ; sinon `null`. */
  rejectionReason?: string | null;
}

/** Pièce qui attend l'élève : demandée et pas encore envoyée, ou refusée et à renvoyer. */
export const awaitsStudent = (request: { status: string }) =>
  request.status === 'requested' || request.status === 'rejected';

export interface LivretSection {
  id: string;
  type: string;
  status: string;
}

export interface LivretLesson {
  id: string;
  type: string | null;
  startsAt: string;
  durationMinutes: number;
}

export interface CandidateLivret {
  sections: LivretSection[];
  lessons: LivretLesson[];
}

export interface CompetencyProgress {
  label: string;
  level: string;
  percent: number;
}

export interface CandidateProgress {
  competencies: CompetencyProgress[];
  readyForExam: boolean;
  latestRating: number | null;
}

export interface CandidateExam {
  date: string;
  center: string | null;
  status: string | null;
}

export interface CandidateBilan {
  id: string;
  date: string;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
  instructorPhone?: string | null;
  instructorEmail?: string | null;
  summary: string;
  rating: number | null;
  readyForExam: boolean;
}

// ─── Livret d'apprentissage (REMC) ──────────────────────────────────────────
export type LearningProgression = 'aborde' | 'traite' | 'assimile';

export interface LearningBookletItem {
  code: string;
  label: string;
  progression: LearningProgression | null;
  selfAssessment: number | null;
}
export interface LearningBookletCategory {
  code: string;
  title: string;
  progressPct: number;
  items: LearningBookletItem[];
}
export interface LearningBooklet {
  family: 'A' | 'B';
  referentialLabel: string;
  licenseCategory: string | null;
  overallPct: number;
  categories: LearningBookletCategory[];
}

// ─── Évaluation de départ ────────────────────────────────────────────────────
export type StartingEvalQuestionType = 'single' | 'multi' | 'boolean_quiz' | 'text' | 'date' | 'quiz' | 'order';
export interface StartingEvalOption { value: string; label: string; }
export interface StartingEvalQuestion {
  code: string;
  label: string;
  type: StartingEvalQuestionType;
  options?: StartingEvalOption[];
  optional?: boolean;
  hint?: string;
  placeholder?: string;
  /** Étapes à mémoriser pendant le chrono, avant de répondre. */
  memorize?: { title: string; text?: string; steps: string[]; seconds: number };
}
export interface StartingEvalSection {
  code: string;
  title: string;
  description?: string;
  scored?: boolean;
  memory?: boolean;
  /** `steps` : une question par écran. */
  layout?: 'page' | 'steps';
  questions: StartingEvalQuestion[];
}
export interface StartingEvalDefinition {
  version: string;
  knowledgeMax: number;
  sections: StartingEvalSection[];
}
export type StartingEvalStatus = 'not_requested' | 'pending' | 'completed';
export interface StartingEvaluation {
  status: StartingEvalStatus;
  requestedAt: string | null;
  completedAt: string | null;
  estimatedHours?: number | null;
  knowledgeScore: number | null;
  knowledgeMax: number | null;
  /** Proposition de l'enseignant, une fois sa partie renseignée. */
  final?: { proposedTotalHours: number; proposedPracticeHours: number } | null;
  definition: StartingEvalDefinition;
  answers: Record<string, string | string[]>;
}
